import { ExclamationCircleOutlined } from "@ant-design/icons"
import { ORANGE } from "@finway-group/shared/lib/consts"
import { Expense, ExpenseStatusEnum, Vendor } from "@finway-group/shared/lib/models"
import { Tooltip } from "antd"
import { AbstractTooltipProps } from "antd/lib/tooltip"
import React from "react"
import { useTranslation } from "react-i18next"
import { useCompany } from "../../../../../shared/hooks/company.hooks"
import { useIsExpenseAccountMandatory } from "../../../../../shared/hooks/expense.hooks"
import { checkIsExpenseReviewable, isExpenseCreditorApproved, isFolderExpense } from "../../../../../shared/utils/expense.utils"
import { ExpenseReviewErrors } from "../../../../../shared/utils/expense.utils.types"

interface ExpenseActionButtonTooltipProps extends AbstractTooltipProps {
    expense: Expense
    subExpenses?: Array<Expense>
    children: React.ReactNode
    shouldShowTooltip?: boolean
    vendors: Array<Vendor> | undefined
}

export const ExpenseActionButtonTooltip = ({ expense, children, subExpenses, vendors, shouldShowTooltip = true, ...rest }: ExpenseActionButtonTooltipProps) => {
    const isExpenseAccountMandatory = useIsExpenseAccountMandatory()
    const company = useCompany()
    const isCompanyGobdCompliant = !!company.gobdCompliance?.enforceGobdCompliantInvoice
    const { t } = useTranslation()

    const checkIsVendorApproved = () => {
        if (!isFolderExpense(expense)) {
            return isExpenseCreditorApproved(expense, vendors)
        }
        return subExpenses?.every((child) => isExpenseCreditorApproved(child, vendors))
    }

    const isVendorApproved = checkIsVendorApproved()

    const mapErrorKeyToTranslationKey = (key: keyof ExpenseReviewErrors) => {
        switch (key) {
            case "postingPeriod":
                return "label:posting_period"
            case "invoiceDate":
                return "label:invoice_date"
            case "invoiceNumber":
                return "label:invoice_no"
            case "creditorNumber":
                return "label:creditor_number"
            case "expenseAccount":
                return "label:expense_account"
            case "description":
                return "label:description"
            case "costCenter":
                return "label:cost_center"
            case "vendor":
                return "label:vendor"
            default:
                return ""
        }
    }

    const MissingFieldError = () => {
        const reviewDiagnostics = !isFolderExpense(expense) ? checkIsExpenseReviewable({ expense, isCompanyGobdCompliant, isExpenseAccountMandatory }) : undefined

        return (
            <div className="m-6">
                <p>{t("tooltips:expenses.not_all_information_provided")}</p>

                {isFolderExpense(expense) && subExpenses?.some((child) => child.status < ExpenseStatusEnum.APPROVED) && <p>{t("tooltips:expenses.not_approved_yet")}</p>}

                {reviewDiagnostics && (
                    <ul className="mt-4">
                        {(Object.entries(reviewDiagnostics.errors) as Array<[keyof ExpenseReviewErrors, boolean]>)
                            .filter(([, isError]) => isError)
                            .map(([key]) => (
                                <li key={key}>
                                    <div className="flex gap-8 content-center">
                                        <ExclamationCircleOutlined style={{ color: ORANGE }} className="mt-4" />
                                        {t(mapErrorKeyToTranslationKey(key))}
                                    </div>
                                </li>
                            ))}
                    </ul>
                )}
            </div>
        )
    }

    const UnapprovedVendorError = () => <div className="m-6">{t("tooltips:expenses.vendor_not_approved")}</div>

    const getTitle = () => {
        if (!shouldShowTooltip) {
            return undefined
        }
        if (!isVendorApproved) {
            return <UnapprovedVendorError />
        }
        return <MissingFieldError />
    }

    return (
        <Tooltip title={getTitle()} {...rest}>
            {children}
        </Tooltip>
    )
}
